import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { offerLine } from '../../utils/offerPricing';
import {
  PHASES, PHASE_STYLE, phaseFor, daysRemaining, msRemaining,
  formatDeadline, formatCountdown,
} from '../../utils/offerDeadline';

/**
 * The promotional-offer banner shown at the top of a pricelist. SHARED by the senior
 * (PackageChooser) and elementary (OptionSelectionContent) flows — one implementation.
 * `offer` is the coupon resolved by useOfferCoupon / for_landing_page.
 *
 * Headline: the admin-authored `promoHeadline` when set, otherwise a derived line from the
 * coupon ("20% off, $20 credit and free shipping. Use coupon FALL20"). This is where the
 * coupon CODE lives — cards show the discount (a struck-through % price, plus an order-level
 * "$X credit at checkout" line for the flat part) but not the code, so without this banner a
 * percentage coupon with no promoHeadline would strike a price with the code nowhere.
 * Renders nothing only when there's no offer AND nothing derivable.
 *
 * FULL-BLEED — spans the full viewport width (edge to edge) regardless of the centered
 * container it sits in, via the `w-screen` + `margin-left: calc(50% - 50vw)` breakout.
 * STICKY — it scrolls up WITH the packages until it reaches the top of the viewport, then
 * pins there (`sticky top-0`) while the packages keep scrolling up and off underneath it
 * (`z-10` keeps the banner above the cards). The customer nav is in-flow (not fixed), so
 * `top-0` pins to the true viewport top after the nav scrolls away; the app shell uses
 * `overflow-x: clip` (not `hidden`) specifically so no ancestor traps this sticky element.
 * `className` is just for per-caller spacing (e.g. `mt-2`).
 */
// Delivery-truck glyph (Tabler "truck", inlined so we don't add an icon
// dependency for one use). Shown next to the headline when the coupon
// waives shipping. Substring-matches the coupon_type to agree with the
// Rails Coupon#free_shipping? check. Sized/coloured to sit one step
// behind the offer text in the hierarchy; hidden on <768px where the
// bar is already tight.
const TruckIcon = () => (
  <svg
    className="inline-block max-md:hidden ml-[10px] align-[-6px]"
    width="22" height="22" viewBox="0 0 24 24"
    fill="none" stroke="#B5D4F4" strokeWidth="1.75"
    strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    <path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" />
    <path d="M5 17h-2v-4m-1 -8h11v12m-4 0h6m4 0h2v-6h-8m0 -5h5l3 5" />
    <path d="M3 9l4 0" />
  </svg>
);

/**
 * The final-day countdown, isolated so PromoBanner stays a pure function.
 *
 * Only this piece needs to re-render every second, and only on the last day.
 * Hooks in the banner itself would also break its tests, which call it
 * directly rather than mounting it -- a fair signal that the ticking belongs
 * somewhere smaller.
 */
function Countdown({ expiresOn, fallback }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const left = formatCountdown(msRemaining(expiresOn, now));
  // The parent computes `phase` once at render and never recomputes it, so a
  // tab left open across the expiry moment would keep the red styling while
  // this returned nothing -- a red bar with no words in it. Falling back to the
  // offer line means the worst case is a stale banner rather than an empty one.
  return <>{left ? `ENDS IN ${left}` : fallback}</>;
}

Countdown.propTypes = {
  expiresOn: PropTypes.string.isRequired,
  fallback: PropTypes.node,
};

Countdown.defaultProps = { fallback: null };

export default function PromoBanner({ offer, className }) {
  if (!offer) return null;
  const { expiresOn } = offer;
  const headline = offer.promoHeadline || offerLine(offer);
  if (!headline) return null;
  const includesFreeShipping = !!offer.couponType && offer.couponType.includes('FREE_SHIPPING');

  const phase = phaseFor(expiresOn);
  const deadline = formatDeadline(expiresOn);
  const days = daysRemaining(expiresOn);

  // The urgency prefix rides ON the operator's headline rather than replacing
  // it -- the offer itself ("20% off + free shipping") must survive every
  // phase, or the loudest banner is the one that stops saying what it gives.
  // REMINDER and URGENT are ONE countdown -- only the colour escalates between
  // them (PHASE_STYLE). Phrasing them differently ("LAST DAY IN 2") also meant
  // counting differently, so the banner jumped 5 DAYS LEFT -> LAST DAY IN 3 and
  // appeared to skip a day. `days` counts today, so an offer whose final day is
  // tomorrow reads 2 DAYS LEFT -- you do still have today.
  let prefix = null;
  if (phase === PHASES.REMINDER || phase === PHASES.URGENT) {
    prefix = `${days} ${days === 1 ? 'DAY' : 'DAYS'} LEFT — `;
  }

  // Falls back to the operator's static subhead when the server sent no date
  // (any path that is not for_landing_page), so this can never render blank.
  let subhead = offer.promoSubhead;
  if (deadline && phase === PHASES.VALUE) subhead = `Applied at checkout · Offer good through ${deadline}`;
  if (deadline && (phase === PHASES.REMINDER || phase === PHASES.URGENT)) {
    subhead = `Offer ends ${deadline} at midnight`;
  }
  if (phase === PHASES.FINAL) {
    subhead = `Last chance for ${headline.toLowerCase()} — applied at checkout`;
  }

  const style = PHASE_STYLE[phase] || PHASE_STYLE[PHASES.VALUE];

  // The offer itself, used both as the normal headline and as the countdown's
  // fallback once the clock runs out.
  const offerLabel = (
    <>
      {prefix}
      {headline}
      {includesFreeShipping && <TruckIcon />}
    </>
  );

  return (
    <div
      className={`sticky top-0 z-10 w-screen ml-[calc(50%_-_50vw)] px-5 py-2.5 text-center shadow-md ${className}`}
      style={style}
    >
      <p className="text-base sm:text-lg font-extrabold text-white uppercase tracking-widest">
        {phase === PHASES.FINAL ? (
          <Countdown expiresOn={expiresOn} fallback={offerLabel} />
        ) : offerLabel}
      </p>
      {subhead && (
        <p className="mt-0.5 text-xs font-medium text-white uppercase tracking-widest">{subhead}</p>
      )}
    </div>
  );
}

PromoBanner.defaultProps = { offer: null, className: 'mt-2' };

PromoBanner.propTypes = {
  offer: PropTypes.shape({
    promoHeadline: PropTypes.string,
    promoSubhead: PropTypes.string,
    // "2026-08-21" -- this student's own deadline, from their sitting. Absent
    // on any path that does not know the student; the banner then shows the
    // static subhead as before.
    expiresOn: PropTypes.string,
    // Used to derive a headline when promoHeadline is absent (offerLine).
    couponType: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flatValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
  }),
  // Per-flow width + corner-radius so the banner matches that flow's cards.
  className: PropTypes.string,
};
